import React from 'react';

import { regionURL } from '@admin/config/routes';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { MutedPanel } from '@admin/styled/muted_panel';
import { MutedText } from '@admin/styled/muted_text';
import { Section } from '@admin/styled/section';
import { SectionTitle } from '@admin/styled/section_title';
import { Table } from '@admin/styled/table';
import { TableWrap } from '@admin/styled/table_wrap';

export type FacilityRegionDefaultRow = {
  id: string;
  name: string;
  active: boolean;
  defaultWarehouse?: { id: string; name: string } | null;
  defaultDepot?: { id: string; name: string } | null;
};

type FacilityRegionDefaultsProps = {
  regions: FacilityRegionDefaultRow[];
  warehouseId?: string | null;
  depotId?: string | null;
  warehouseActive?: boolean;
  depotActive?: boolean;
};

function RegionList({ regions }: { regions: FacilityRegionDefaultRow[] }) {
  if (regions.length === 0) {
    return <MutedText>None</MutedText>;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
      {regions.map((region) => (
        <ButtonLink key={region.id} to={regionURL({ id: region.id })}>
          {region.name}
        </ButtonLink>
      ))}
    </div>
  );
}

export const FacilityRegionDefaults: React.FC<FacilityRegionDefaultsProps> = ({
  regions,
  warehouseId,
  depotId,
  warehouseActive = false,
  depotActive = false,
}) => {
  const showConsumerStorage = Boolean(warehouseId);
  const showConsumerDepot = Boolean(depotId);

  if (!showConsumerStorage && !showConsumerDepot) {
    return null;
  }

  const storageDefaultRegions = regions.filter(
    (region) =>
      region.active &&
      warehouseActive &&
      warehouseId != null &&
      region.defaultWarehouse?.id != null &&
      String(region.defaultWarehouse.id) === String(warehouseId),
  );

  const depotDefaultRegions = regions.filter(
    (region) =>
      region.active &&
      depotActive &&
      depotId != null &&
      region.defaultDepot?.id != null &&
      String(region.defaultDepot.id) === String(depotId),
  );

  return (
    <Section>
      <SectionTitle>Active regions for which this facility is a default</SectionTitle>

      {regions.length === 0 ? (
        <Card>
          <MutedPanel>Add regions on this facility&apos;s overview to see default assignments.</MutedPanel>
        </Card>
      ) : (
        <Card>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Regions</th>
                </tr>
              </thead>
              <tbody>
                {showConsumerStorage && (
                  <tr>
                    <td>Consumer Storage</td>
                    <td>
                      <RegionList regions={storageDefaultRegions} />
                    </td>
                  </tr>
                )}
                {showConsumerDepot && (
                  <tr>
                    <td>Consumer Depot</td>
                    <td>
                      <RegionList regions={depotDefaultRegions} />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}
    </Section>
  );
};
